import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TERM_TYPES = [
  { value: "preferred", label: "Preferred Term" },
  { value: "banned", label: "Banned Term" },
  { value: "replacement", label: "Replacement" },
];

export default function StyleGuideManager() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    term_type: "preferred",
    original_term: "",
    preferred_term: "",
    department: "",
  });

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/ai-editor/style-guide`, {
        headers: getHeaders(), withCredentials: true
      });
      setTerms(res.data.items || []);
    } catch {}
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!form.original_term.trim()) {
      toast.error("Original term is required");
      return;
    }
    try {
      await axios.post(`${API}/api/ai-editor/style-guide`, form, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success("Term added to style guide");
      setShowForm(false);
      setForm({ term_type: "preferred", original_term: "", preferred_term: "", department: "" });
      fetchTerms();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add term");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/ai-editor/style-guide/${id}`, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success("Term removed");
      fetchTerms();
    } catch (err) {
      toast.error("Failed to delete term");
    }
  };

  const getTypeBadge = (type) => {
    const t = TERM_TYPES.find((tt) => tt.value === type);
    const colors = {
      preferred: "bg-blue-100 text-blue-700",
      banned: "bg-red-100 text-red-700",
      replacement: "bg-green-100 text-green-700",
    };
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors[type] || colors.preferred}`}>
        {t?.label || type}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <BookOpen className="w-4 h-4" />
          {terms.length} term{terms.length !== 1 ? "s" : ""} in style guide
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          size="sm"
          className="bg-cyan-600 hover:bg-cyan-500 rounded-none text-xs h-7"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Term
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="border border-cyan-200 bg-cyan-50 p-4 rounded space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Term Type</label>
              <select
                value={form.term_type}
                onChange={(e) => setForm({ ...form, term_type: e.target.value })}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                {TERM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Department (optional)</label>
              <Input
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                placeholder="All departments"
                className="rounded-none text-sm h-8"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Original Term</label>
              <Input
                value={form.original_term}
                onChange={(e) => setForm({ ...form, original_term: e.target.value })}
                placeholder='e.g. "Obaise"'
                className="rounded-none text-sm h-8"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Preferred Term (for replacements)</label>
              <Input
                value={form.preferred_term}
                onChange={(e) => setForm({ ...form, preferred_term: e.target.value })}
                placeholder='e.g. "AllBaise"'
                className="rounded-none text-sm h-8"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm" className="rounded-none text-xs h-7">
              Cancel
            </Button>
            <Button onClick={handleAdd} size="sm" className="bg-cyan-600 hover:bg-cyan-500 rounded-none text-xs h-7">
              Add to Style Guide
            </Button>
          </div>
        </div>
      )}

      {/* Terms table */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : terms.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No style guide terms yet. Add your first term above.</p>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-xs text-slate-600">
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Original</th>
                <th className="text-left px-3 py-2 font-medium">Preferred</th>
                <th className="text-left px-3 py-2 font-medium">Department</th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {terms.map((term) => (
                <tr key={term.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">{getTypeBadge(term.term_type)}</td>
                  <td className="px-3 py-2 font-medium text-slate-700">{term.original_term}</td>
                  <td className="px-3 py-2 text-slate-600">{term.preferred_term || "—"}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{term.department || "All"}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => handleDelete(term.id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
